import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  Button,
  Checkbox,
  Form,
  Modal,
  Segment,
  Table,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import type {
  IpAllowListEntry,
  OnOrganisationEditedFn,
  SettingsOwnerOrganisation,
  ValidateIpActionRequest,
} from "../../types";

type IpRestrictionProps = { userIp: string };

const IpRestriction = ({ userIp }: IpRestrictionProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onOrganisationEdited = useCallback<OnOrganisationEditedFn>(
    (field, value) => dispatch(actions.user.updateOrganisation(field, value)),
    [dispatch],
  );

  const ownerOrganisation = user?.ownerOrganisation as
    | SettingsOwnerOrganisation
    | undefined;

  const [newIpModalOpen, setNewIpModalOpen] = useState(false);
  const [newAddress, setNewAddress] = useState<IpAllowListEntry | null>(null);

  const updateIpRestriction = useCallback(
    (enabled?: boolean) => {
      if (!ownerOrganisation?._id) return;
      const ipRestriction = { ...ownerOrganisation.ipRestriction };
      ipRestriction.isEnabled = enabled;
      if (enabled) {
        util
          .confirm(
            "Enable IP address restriction?",
            `This will prevent access to ${util.appName()} for any users outside of the IP ranges specified.`,
          )
          .then(() => {
            api.organisations.updateSettings(
              ownerOrganisation._id,
              { ipRestriction },
              () => {
                onOrganisationEdited("ipRestriction", ipRestriction);
                toast.success(t("generic.typeUpdated", { type: "Settings" }));
              },
              () => {},
            );
          })
          .catch(() => {});
      } else {
        api.organisations.updateSettings(
          ownerOrganisation._id,
          { ipRestriction },
          () => {
            onOrganisationEdited("ipRestriction", ipRestriction);
            toast.success(t("generic.typeUpdated", { type: "Settings" }));
          },
          () => {},
        );
      }
    },
    [onOrganisationEdited, t, ownerOrganisation],
  );

  const deleteIpAddress = useCallback(
    (ip: IpAllowListEntry) => {
      if (!ownerOrganisation?._id) return;
      const ipRestriction = { ...ownerOrganisation.ipRestriction };
      const allowList = [...(ipRestriction.allowList || [])];
      const index = allowList.findIndex(
        (entry) => entry.address === ip.address,
      );
      if (index > -1) {
        allowList.splice(index, 1);
      }
      ipRestriction.allowList = allowList;
      if (allowList.length === 0) {
        ipRestriction.isEnabled = false;
      }
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { ipRestriction },
        () => {
          onOrganisationEdited("ipRestriction", ipRestriction);
          toast.success("IP address removed");
        },
        () => {},
      );
    },
    [onOrganisationEdited, ownerOrganisation],
  );

  const addIpAddress = useCallback(() => {
    if (!ownerOrganisation?._id || !newAddress) return;
    const ipRestriction = { ...ownerOrganisation.ipRestriction };
    const allowList = [...(ipRestriction?.allowList || [])];
    allowList.push(newAddress);
    ipRestriction.allowList = allowList;
    api.organisations.updateSettings(
      ownerOrganisation._id,
      { ipRestriction },
      () => {
        onOrganisationEdited("ipRestriction", ipRestriction);
        toast.success("IP address added");
        setNewIpModalOpen(false);
        setNewAddress(null);
      },
      () => {},
    );
  }, [onOrganisationEdited, ownerOrganisation, newAddress]);

  const checkValidIpAction = useCallback(
    (
      ip: IpAllowListEntry | null,
      action: "validateIpAddress" | "validateEnableIpRestriction",
      callback: (confirmed?: boolean) => void,
    ) => {
      if (!ownerOrganisation?._id) return;
      api.organisations.checkValidIpAction(
        ownerOrganisation._id,
        { ip, action } as ValidateIpActionRequest,
        (data) => {
          if (data.success) callback(true);
          else if (data.failType === "addressOutOfRange") {
            util
              .confirm(
                "Add to allow list?",
                "The address you have entered does not match your current IP address, are you sure you want to add this.",
              )
              .then(() => {
                callback();
              })
              .catch(() => {});
          } else if (data.failType === "lockOutWarning") {
            util
              .confirm(
                "Enable IP address restriction?",
                "Your current IP address is outside of the IP ranges specified in the allow list, if you enable this setting your account will not be able to access the platform.",
              )
              .then(() => {
                callback(true);
              })
              .catch(() => {});
          }
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [ownerOrganisation],
  );

  if (!ownerOrganisation) {
    return null;
  }

  const ipRestrictionDisabled =
    (ownerOrganisation.ipRestriction?.allowList?.length ?? 0) === 0;
  const ipRestrictionEnabled = ownerOrganisation.ipRestriction?.isEnabled;

  return (
    <>
      <Segment>
        <h3>IP address restriction</h3>
        <p>
          Restrict access to {util.appName()} by IP address.
          {userIp ?
            <>
              {" "}
              Your current IP address is <b>{userIp}</b>.
            </>
          : null}
        </p>
        <Checkbox
          toggle
          label="Enable IP restriction"
          checked={ipRestrictionEnabled}
          onChange={(_e, c) =>
            ipRestrictionEnabled ?
              updateIpRestriction(c.checked)
            : checkValidIpAction(
                null,
                "validateEnableIpRestriction",
                updateIpRestriction,
              )
          }
          disabled={ipRestrictionDisabled}
          data-tooltip={
            ipRestrictionDisabled ?
              "Setting is disabled because no IP addresess have been added to the allow list"
            : null
          }
        />
        <h5>Allowed IP addresses and CIDR blocks</h5>
        <p>Only allow logins from IP addresses within the following list.</p>
        <Button
          content="Add IP address to allow list"
          primary
          onClick={() => setNewIpModalOpen(true)}
        />
        {(ownerOrganisation.ipRestriction?.allowList?.length ?? 0) > 0 && (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>IP address or CIDR block</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {ownerOrganisation.ipRestriction?.allowList?.map((ip) => (
                <Table.Row key={ip.address}>
                  <Table.Cell>{ip.address}</Table.Cell>
                  <Table.Cell>{ip.description}</Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    <Button
                      icon="trash"
                      size="mini"
                      basic
                      compact
                      onClick={() => deleteIpAddress(ip as IpAllowListEntry)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={newIpModalOpen}
      >
        <Modal.Header>Add allowed address</Modal.Header>
        <Modal.Content>
          <p>
            Add an allowed address using the CIDR notation or as a single IP
            address.
          </p>
          <Form>
            <Form.Field>
              <label htmlFor="ipAddress">IP address or CIDR block</label>
              <Input
                id="ipAddress"
                value={newAddress?.address ?? ""}
                placeholder="0.0.0.0/0"
                onChange={(e) => {
                  setNewAddress((prevState) => ({
                    address: e.target.value,
                    description: prevState?.description,
                  }));
                }}
              />
            </Form.Field>

            <Form.Field>
              <label htmlFor="ipDescription">Description</label>
              <Input
                id="ipDescription"
                value={newAddress?.description ?? ""}
                placeholder="Optional comment describing the entry"
                onChange={(e) => {
                  setNewAddress((prevState) => ({
                    address: prevState?.address ?? "",
                    description: e.target.value,
                  }));
                }}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            basic
            color="grey"
            size="small"
            content="Cancel"
            onClick={() => setNewIpModalOpen(false)}
          />
          <Button
            secondary
            size="small"
            content="Confirm"
            onClick={() =>
              checkValidIpAction(newAddress, "validateIpAddress", addIpAddress)
            }
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default IpRestriction;
